import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle, XCircle } from "lucide-react";

/* ===== Badge حالة الاشتراك ===== */
function SubscriptionBadge({ row }) {
  if (!row.plan) {
    return (
      <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-500">
        بدون اشتراك
      </span>
    );
  }

  if (row.days_left < 0) {
    return (
      <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-600 flex items-center gap-1">
        <XCircle size={14} /> منتهي
      </span>
    );
  }

  if (row.days_left <= 30) {
    return (
      <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700 flex items-center gap-1">
        <AlertTriangle size={14} /> {row.days_left} يوم
      </span>
    );
  }

  return (
    <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 flex items-center gap-1">
      <CheckCircle size={14} /> فعّال
    </span>
  );
}

/* ===== Modal إدارة الاشتراك ===== */
function SubscriptionModal({ row, onClose, onSaved }) {
  const [plan, setPlan] = useState(row.plan || "");
  const [startDate, setStartDate] = useState(
    row.start_date || new Date().toISOString().slice(0, 10)
  );
  const [endDate, setEndDate] = useState(
    row.end_date ||
      new Date(new Date().setFullYear(new Date().getFullYear() + 1))
        .toISOString()
        .slice(0, 10)
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch("/api/admin/subscription-save.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          website_id: row.id,
          plan,
          start_date: startDate,
          end_date: endDate,
        }),
      });

      const json = await res.json();
      if (!json.ok) {
        alert("فشل حفظ الاشتراك");
        return;
      }

      onSaved();
      onClose();
    } catch {
      alert("تعذر الاتصال بالسيرفر");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-md rounded-2xl p-6 space-y-4">
        <h3 className="text-lg font-extrabold">
          إدارة الاشتراك — {row.domain}
        </h3>

        <div>
          <label className="text-sm">الخطة</label>
          <input
            value={plan}
            onChange={e => setPlan(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 mt-1"
            placeholder="Basic / Pro / Business"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm">تاريخ البداية</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 mt-1"
            />
          </div>

          <div>
            <label className="text-sm">تاريخ النهاية</label>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 mt-1"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border rounded-lg"
          >
            إلغاء
          </button>

          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-[#4A9FD8] text-white disabled:opacity-50"
          >
            {saving ? "جارٍ الحفظ…" : "حفظ الاشتراك"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===== الصفحة الرئيسية للمواقع ===== */
export default function AdminWebsites() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const loadData = () => {
    setLoading(true);
    fetch("/api/admin/websites.php")
      .then(res => res.json())
      .then(json => {
        if (json.ok) setRows(json.data || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-extrabold">المواقع</h2>
        <p className="text-sm text-slate-500">
          إدارة المواقع والاشتراكات المرتبطة بها
        </p>
      </div>

      <div className="bg-white border rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-right">النطاق</th>
              <th className="px-4 py-3 text-center">الخطة</th>
              <th className="px-4 py-3 text-center">الاشتراك</th>
              <th className="px-4 py-3 text-center">تاريخ الانتهاء</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan="4" className="px-4 py-6 text-center text-slate-500">
                  جاري التحميل…
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan="4" className="px-4 py-6 text-center text-slate-500">
                  لا توجد مواقع
                </td>
              </tr>
            )}

            {rows.map(row => (
              <tr key={row.id} className="border-t">
                <td className="px-4 py-3 font-mono">{row.domain}</td>

                <td className="px-4 py-3 text-center">
                  {row.plan || "—"}
                </td>

                <td className="px-4 py-3 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <SubscriptionBadge row={row} />
                    <button
                      onClick={() => setEditing(row)}
                      className="text-xs text-[#4A9FD8] hover:underline"
                    >
                      إدارة الاشتراك
                    </button>
                  </div>
                </td>

                <td className="px-4 py-3 text-center">
                  {row.end_date || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <SubscriptionModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={loadData}
        />
      )}
    </div>
  );
}
